import React, {useMemo} from 'react';
import {Box, Typography, Button} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {FormikErrors} from 'formik';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import AutoField, {
  ActionType,
  FieldValue,
  Option,
} from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackTemplateTypes,
  ReportPackContactInput,
  GlobalPermissionEnum,
} from '../../schema-types/globalTypes';
import {useReportPack} from './useReportPack';
import {useReviewContext} from '../../contexts/ReviewContext';
import {getReviewContacts} from './UploadDocumentReportPack';
import {useAccountContext} from '../../contexts/AccountContext';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {useFeaturePermission} from '../../contexts/FeatureBoundary';
import {UpsertReportPackVariables} from '../../schema-types/UpsertReportPack';

type NewReportPackFormType = {
  type: 'template' | 'upload' | null;
  templateType: ReportPackTemplateTypes | null;
};

type NewReportPackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const validateNewReportPackForm = (values: NewReportPackFormType) => {
  const errors: FormikErrors<NewReportPackFormType> = {};

  if (!values.type) {
    errors.type = 'Please select a report pack type';
  }

  if (values.type === 'template' && !values.templateType) {
    errors.templateType = 'Please select a template type';
  }

  return errors;
};

const NewReportPackForm: React.FC<NewReportPackModalProps> = ({isOpen, onClose}) => {
  const {onUpsert} = useReportPack();
  const {reviewId, reviewDetails} = useReviewContext();
  const {account} = useAccountContext();
  const navigate = useNavigate();
  const canUseNoAction = useFeaturePermission([
    GlobalPermissionEnum.can_access_report_pack_no_action_letter_FE,
  ]);
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const reviewContacts = useMemo(
    () => getReviewContacts(reviewDetails, account, null),
    [reviewDetails, account]
  );

  // Determine initial values based on review type and characteristics
  const initialValueOverrides = useMemo(() => {
    const hasRecommendations = !!reviewDetails?.validation?.recs?.length; // No rec validation errors means recs exist

    // Only set defaults when there are clear indicators
    let defaultType: 'template' | 'upload' | null = null;
    let defaultTemplateType: ReportPackTemplateTypes | null = null;

    // If review has recommendations, suggest attachment letter with template
    if (hasRecommendations || !canUseNoAction) {
      defaultType = 'upload';
      defaultTemplateType = ReportPackTemplateTypes.ATTACHMENT_LETTER;
    }

    // For reviews without recommendations, suggest no action letter with template
    if (!hasRecommendations && canUseNoAction) {
      defaultType = 'template';
      defaultTemplateType = ReportPackTemplateTypes.NO_ACTION_LETTER;
    }

    return {
      type: defaultType,
      templateType: defaultTemplateType,
    };
  }, [reviewDetails, canUseNoAction]);

  const handleClose = () => {
    onClose();
    navigate(`/account/${account?.id}/review-journey/${reviewId}/summary`);
  };

  const handleSubmit = async (values: NewReportPackFormType) => {
    const variables: UpsertReportPackVariables = {
      reviewId,
      contacts:
        reviewContacts.map<ReportPackContactInput>((contact) => ({
          contactCuroId: contact.curoId,
          contactStatus: contact.contactStatus!,
        })) || [],
    };

    // Add template type if it's a template
    if (values.type === 'template' && values.templateType) {
      variables.templateType = values.templateType;
      variables.templateHtml = ''; // Placeholder for template HTML
    }

    try {
      enqueueSnackbar('Creating report pack...', {variant: 'info', persist: true});

      const {data} = await onUpsert({
        variables,
      });
      if (data?.upsertReportPack?.ok) {
        closeSnackbar(); // Close the snackbar after successful creation
        enqueueSnackbar('Successfully created report pack', {variant: 'success'});
        onClose(); // Close modal without navigation
      } else {
        throw data?.upsertReportPack?.errors?.join('...') || 'Unknown error';
      }
    } catch (error) {
      closeSnackbar(); // Close the snackbar after successful creation
      enqueueSnackbar(`Failed to create report pack: ${error}`, {
        variant: 'error',
        persist: true,
      });
    }
  };

  return (
    <ScrollableModal size="large" open={isOpen} onClose={handleClose}>
      <Box p={3} display="flex" flexDirection="column" height="100%">
        <Typography variant="h4" mb={3}>
          Create report pack
        </Typography>

        <NewReportPackFormWrapped
          formFields={[]}
          submit={handleSubmit}
          isOpen={isOpen}
          onClose={handleClose}
          initialValueOverrides={initialValueOverrides}
        />
      </Box>
    </ScrollableModal>
  );
};

const NewReportPackFormInner: React.FC<NewReportPackModalProps> = () => {
  const {submitForm, values} = useAutoForm<NewReportPackFormType>();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const canUseNoAction = useFeaturePermission([
    GlobalPermissionEnum.can_access_report_pack_no_action_letter_FE,
  ]);
  const canUseDeclinedUncontactable = useFeaturePermission([
    GlobalPermissionEnum.can_access_report_pack_declined_uncontactable_letter_FE,
  ]);
  let templateTypeOptions = [
    {
      label: 'No action',
      value: ReportPackTemplateTypes.NO_ACTION_LETTER,
    },
  ];
  if (canUseDeclinedUncontactable) {
    templateTypeOptions = [
      ...templateTypeOptions,
      {
        label: 'Declined',
        value: ReportPackTemplateTypes.DECLINED_LETTER,
      },
      {
        label: 'Uncontactable',
        value: ReportPackTemplateTypes.UNCONTACTABLE_LETTER,
      },
    ];
  }
  const options = useMemo<Option<FieldValue>[]>(() => {
    const defaultOptions: Option<FieldValue>[] = [
      {label: 'Upload file', value: 'upload'},
    ];
    if (canUseNoAction) {
      defaultOptions.push({label: 'Report template', value: 'template'});
    }
    return defaultOptions;
  }, [canUseNoAction]);

  return (
    <Box>
      <Box mb={3}>
        <AutoField
          field={{
            id: 'type',
            label: 'Report Pack Type',
            boldLabel: true,
            fullWidth: true,
            type: ActionType.radioGroup,
            options,
          }}
        />
      </Box>

      {canUseNoAction && values.type === 'template' && (
        <Box mb={3}>
          <AutoField
            field={{
              id: 'templateType',
              label: 'Template type',
              boldLabel: true,
              type: ActionType.select,
              options: templateTypeOptions,
            }}
          />
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          onClick={() => {
            closeSnackbar();
            const errors = validateNewReportPackForm(values);
            if (Object.keys(errors).length > 0) {
              Object.entries(errors).forEach(([_, error]) => {
                enqueueSnackbar(`${error}`, {variant: 'error'});
              });
              return;
            }
            // If no errors, submit the form
            submitForm();
          }}
        >
          Create
        </Button>
      </Box>
    </Box>
  );
};

const NewReportPackFormWrapped = withAutoFormNew<
  NewReportPackFormType,
  NewReportPackModalProps
>(NewReportPackFormInner, {});

export {NewReportPackForm as NewReportPackModal};
